/**
 * Client options - re-exports from shared package plus UI-specific Option arrays
 */

// Re-export everything from shared package
export * from '@couple/shared-options';

// Import constants and labels for generating Option arrays
import {
  BODY_TYPES,
  FREQUENCY_LEVELS,
  EXERCISE_LEVELS,
  HAS_KIDS_VALUES,
  WANTS_MORE_KIDS_VALUES,
  CHILD_GENDERS,
  CUSTODY_VALUES,
  KIDS_YOUNGEST_AGES,
  KIDS_COUNTS,
  ETHNICITIES,
  POLITICAL_VIEWS,
  EDUCATION_LEVELS,
  EMPLOYMENT_STATUSES,
  INCOME_RANGES,
  RELATIONSHIP_STRUCTURES,
  RELATIONSHIP_INTENTS,
  PRONOUNS,
  FLEXIBILITY_OPTIONS,
  INTERESTED_IN_GENDERS,
  PREFERRED_STRUCTURES,
  SEXUAL_ROLES,
  SEXUAL_DYNAMICS,
  SEXUAL_ORIENTATIONS,
  PET_TYPES,
  RELIGIONS,
  LANGUAGES,
  ZODIAC_SIGNS,
  CAREER_FIELDS,
  MAJORS,
} from '@couple/shared-options';

import {
  BODY_TYPE_LABELS,
  FREQUENCY_LABELS,
  EXERCISE_LEVEL_LABELS,
  HAS_KIDS_LABELS,
  WANTS_MORE_KIDS_LABELS,
  CHILD_GENDER_LABELS,
  CUSTODY_LABELS,
  ETHNICITY_LABELS,
  POLITICAL_VIEW_LABELS,
  EDUCATION_LEVEL_LABELS,
  EMPLOYMENT_STATUS_LABELS,
  INCOME_RANGE_LABELS,
  RELATIONSHIP_STRUCTURE_LABELS,
  RELATIONSHIP_INTENT_LABELS,
  PRONOUNS_LABELS,
  FLEXIBILITY_LABELS,
  INTERESTED_IN_LABELS,
  PREFERRED_STRUCTURE_LABELS,
  SEXUAL_ROLE_LABELS,
  SEXUAL_DYNAMICS_LABELS,
  SEXUAL_ORIENTATION_LABELS,
  PET_TYPE_LABELS,
  RELIGION_LABELS,
  LANGUAGE_LABELS,
  ZODIAC_SIGN_LABELS,
  CAREER_FIELD_LABELS,
  MAJOR_LABELS,
} from '@couple/shared-options';

// ============================================================================
// OPTION INTERFACE (for dropdowns/selects)
// ============================================================================

export interface Option {
  value: string;
  label: string;
}

// Helper to generate Option arrays from const arrays and label maps
function toOptions<T extends readonly string[]>(
  values: T,
  labels: Record<string, string>,
  placeholder?: string
): Option[] {
  const options: Option[] = placeholder ? [{ value: '', label: placeholder }] : [];
  for (const v of values) {
    options.push({ value: v, label: labels[v] || v });
  }
  return options;
}

// ============================================================================
// OPTION ARRAYS (for UI dropdowns)
// ============================================================================

export const BODY_TYPE_OPTIONS: Option[] = toOptions(BODY_TYPES, BODY_TYPE_LABELS, 'Select body type');
export const FREQUENCY_OPTIONS: Option[] = toOptions(FREQUENCY_LEVELS, FREQUENCY_LABELS, 'Select');
export const EXERCISE_OPTIONS: Option[] = toOptions(EXERCISE_LEVELS, EXERCISE_LEVEL_LABELS, 'Select');
export const HAS_KIDS_OPTIONS: Option[] = toOptions(HAS_KIDS_VALUES, HAS_KIDS_LABELS, 'Select');
export const WANTS_MORE_KIDS_OPTIONS: Option[] = toOptions(WANTS_MORE_KIDS_VALUES, WANTS_MORE_KIDS_LABELS, 'Select');
export const CHILD_GENDER_OPTIONS: Option[] = toOptions(CHILD_GENDERS, CHILD_GENDER_LABELS);
export const CUSTODY_OPTIONS: Option[] = toOptions(CUSTODY_VALUES, CUSTODY_LABELS, 'Select custody');

export const KIDS_YOUNGEST_AGE_OPTIONS: Option[] = KIDS_YOUNGEST_AGES.map(v => ({ value: v, label: v }));
export const KIDS_COUNT_OPTIONS: Option[] = KIDS_COUNTS.map(v => ({ value: v, label: v === '5' ? '5+' : v }));

export const ETHNICITY_OPTIONS: Option[] = toOptions(ETHNICITIES, ETHNICITY_LABELS);
export const POLITICAL_VIEW_OPTIONS: Option[] = toOptions(POLITICAL_VIEWS, POLITICAL_VIEW_LABELS, 'Select political view');

export const EDUCATION_LEVEL_OPTIONS: Option[] = toOptions(EDUCATION_LEVELS, EDUCATION_LEVEL_LABELS, 'Select education');
export const EMPLOYMENT_STATUS_OPTIONS: Option[] = toOptions(EMPLOYMENT_STATUSES, EMPLOYMENT_STATUS_LABELS, 'Select status');
export const INCOME_RANGE_OPTIONS: Option[] = toOptions(INCOME_RANGES, INCOME_RANGE_LABELS, 'Select income');

export const RELATIONSHIP_STRUCTURE_OPTIONS: Option[] = toOptions(RELATIONSHIP_STRUCTURES, RELATIONSHIP_STRUCTURE_LABELS, 'Select structure');
export const RELATIONSHIP_INTENT_OPTIONS: Option[] = toOptions(RELATIONSHIP_INTENTS, RELATIONSHIP_INTENT_LABELS);

export const PRONOUNS_OPTIONS: Option[] = toOptions(PRONOUNS, PRONOUNS_LABELS);
export const FLEXIBILITY_OPTIONS_UI: Option[] = toOptions(FLEXIBILITY_OPTIONS, FLEXIBILITY_LABELS, 'Select');

export const INTERESTED_IN_OPTIONS: Option[] = toOptions(INTERESTED_IN_GENDERS, INTERESTED_IN_LABELS);
export const PREFERRED_STRUCTURE_OPTIONS: Option[] = [
  { value: '', label: 'No preference' },
  ...toOptions(PREFERRED_STRUCTURES, PREFERRED_STRUCTURE_LABELS),
];

export const SEXUAL_ROLE_OPTIONS: Option[] = toOptions(SEXUAL_ROLES, SEXUAL_ROLE_LABELS);
export const SEXUAL_DYNAMICS_OPTIONS: Option[] = toOptions(SEXUAL_DYNAMICS, SEXUAL_DYNAMICS_LABELS);
export const SEXUAL_ORIENTATION_OPTIONS: Option[] = toOptions(SEXUAL_ORIENTATIONS, SEXUAL_ORIENTATION_LABELS);

export const PET_TYPE_OPTIONS: Option[] = toOptions(PET_TYPES, PET_TYPE_LABELS);
export const RELIGION_OPTIONS: Option[] = toOptions(RELIGIONS, RELIGION_LABELS, 'Select religion');
export const LANGUAGES_OPTIONS: Option[] = toOptions(LANGUAGES, LANGUAGE_LABELS);
export const ZODIAC_OPTIONS: Option[] = toOptions(ZODIAC_SIGNS, ZODIAC_SIGN_LABELS, 'Select');
export const CAREER_FIELD_OPTIONS: Option[] = toOptions(CAREER_FIELDS, CAREER_FIELD_LABELS, 'Select career field');
export const MAJOR_OPTIONS: Option[] = toOptions(MAJORS, MAJOR_LABELS, 'Select major');

export const HAS_KIDS_FILTER_OPTIONS: Option[] = [
  { value: '', label: 'No preference' },
  { value: 'must_have', label: 'Has kids' },
  { value: 'must_not_have', label: "Doesn't have kids" },
];

// ============================================================================
// PROFILE COMPLETION
// ============================================================================

/**
 * Count unanswered profile questions by scanning the live DOM.
 * Auto-discovers all radio/checkbox groups in the About tab.
 */
export function countUnansweredFromDOM(): number {
  const tab = document.getElementById('tab-about');
  if (!tab) return 0;

  let unanswered = 0;

  // Every .radio-group in the About tab is one question
  tab.querySelectorAll('.radio-group').forEach(group => {
    if (!isFieldVisible(group)) return;
    const hasChecked = group.querySelector('input[type="radio"]:checked') !== null;
    const pnta = document.querySelector(
      `input[data-pnta-for-group="${group.id}"]`
    ) as HTMLInputElement;
    if (!hasChecked && !pnta?.checked) unanswered++;
  });

  // Every .checkbox-group in the About tab is one question
  tab.querySelectorAll('.checkbox-group').forEach(group => {
    if (!isFieldVisible(group)) return;
    const hasChecked = group.querySelector('input[type="checkbox"]:checked') !== null;
    const pnta = document.querySelector(
      `input[data-pnta-for-group="${group.id}"]`
    ) as HTMLInputElement;
    if (!hasChecked && !pnta?.checked) unanswered++;
  });

  // Every .input-group[data-question] in the About tab is one question
  tab.querySelectorAll('.input-group[data-question]').forEach(group => {
    if (!isFieldVisible(group)) return;
    // Check if any text/number/date input inside has a value
    const inputs = group.querySelectorAll('input.input, textarea.input');
    const hasValue = Array.from(inputs).some(
      input => (input as HTMLInputElement).value.trim() !== ''
    );
    // Check if any PNTA checkbox inside is checked
    const pntaChecked = group.querySelector('.pnta-checkbox input:checked') !== null;
    if (!hasValue && !pntaChecked) unanswered++;
  });

  // Photos: need at least 1
  if (!document.querySelector('#photo-grid .photo-item')) unanswered++;

  return unanswered;
}

/** Skip fields hidden by conditional display (e.g. work fields when unemployed) */
export function isFieldVisible(el: Element): boolean {
  let node: Element | null = el;
  while (node && !node.classList.contains('seg-panel')) {
    if ((node as HTMLElement).style.display === 'none') return false;
    node = node.parentElement;
  }
  return true;
}

// ============================================================================
// SECTION PROGRESS & FIELD ANSWERED STATE
// ============================================================================

/**
 * Update section progress counters (e.g. "2/4") in each collapsible header.
 */
export function updateSectionProgress(): void {
  const tab = document.getElementById('tab-about');
  if (!tab) return;

  tab.querySelectorAll('.form-section').forEach(section => {
    const header = section.querySelector('.form-section-header') as HTMLElement;
    if (!header) return;
    const sectionName = header.dataset.section;
    const progressEl = section.querySelector('.section-progress') as HTMLElement;
    if (!progressEl) return;

    const content = section.querySelector('.form-section-content');
    if (!content) return;

    let total = 0;
    let answered = 0;

    // Special case: photos section
    if (sectionName === 'photos-strip') {
      total = 1;
      answered = document.querySelector('#photo-grid .photo-item') ? 1 : 0;
    } else {
      // Count radio groups
      content.querySelectorAll('.radio-group').forEach(group => {
        if (!isFieldVisible(group)) return;
        total++;
        const hasChecked = group.querySelector('input[type="radio"]:checked') !== null;
        const pnta = document.querySelector(
          `input[data-pnta-for-group="${group.id}"]`
        ) as HTMLInputElement;
        if (hasChecked || pnta?.checked) answered++;
      });

      // Count checkbox groups (skip those inside .intent-entry-card)
      content.querySelectorAll('.checkbox-group').forEach(group => {
        if (group.closest('.intent-entry-card')) return;
        if (!isFieldVisible(group)) return;
        total++;
        const hasChecked = group.querySelector('input[type="checkbox"]:checked') !== null;
        const pnta = document.querySelector(
          `input[data-pnta-for-group="${group.id}"]`
        ) as HTMLInputElement;
        if (hasChecked || pnta?.checked) answered++;
      });

      // Count text/date/number inputs with [data-question]
      content.querySelectorAll('.input-group[data-question]').forEach(group => {
        if (!isFieldVisible(group as Element)) return;
        total++;
        const inputs = group.querySelectorAll('input.input, textarea.input');
        const hasValue = Array.from(inputs).some(
          input => (input as HTMLInputElement).value.trim() !== ''
        );
        const pntaChecked = group.querySelector('.pnta-checkbox input:checked') !== null;
        if (hasValue || pntaChecked) answered++;
      });
    }

    // Skip sections with no countable fields (e.g., "What I'm Looking For")
    if (total === 0) {
      progressEl.textContent = '';
      return;
    }

    progressEl.textContent = `${answered}/${total}`;
    progressEl.classList.toggle('complete', answered === total);
  });
}

/**
 * Toggle `.answered` class on each `.input-group` based on whether the field has a value.
 * CSS uses this class to show a subtle checkmark via ::after pseudo-element.
 */
export function updateFieldAnsweredStates(): void {
  const tab = document.getElementById('tab-about');
  if (!tab) return;

  tab.querySelectorAll('.input-group').forEach(group => {
    if (!isFieldVisible(group)) {
      group.classList.remove('answered');
      return;
    }

    let isAnswered = false;

    // Check radio groups inside
    const radioGroup = group.querySelector('.radio-group');
    if (radioGroup) {
      const hasChecked = radioGroup.querySelector('input[type="radio"]:checked') !== null;
      const pnta = document.querySelector(
        `input[data-pnta-for-group="${radioGroup.id}"]`
      ) as HTMLInputElement;
      isAnswered = hasChecked || !!pnta?.checked;
    }

    // Check checkbox groups inside (skip intent entry cards)
    const checkboxGroup = group.querySelector('.checkbox-group');
    if (checkboxGroup && !radioGroup && !checkboxGroup.closest('.intent-entry-card')) {
      const hasChecked = checkboxGroup.querySelector('input[type="checkbox"]:checked') !== null;
      const pnta = document.querySelector(
        `input[data-pnta-for-group="${checkboxGroup.id}"]`
      ) as HTMLInputElement;
      isAnswered = hasChecked || !!pnta?.checked;
    }

    // Check text/date/number inputs (only for [data-question] groups without radio/checkbox)
    if (group.hasAttribute('data-question') && !radioGroup && !checkboxGroup) {
      const inputs = group.querySelectorAll('input.input, textarea.input');
      const hasValue = Array.from(inputs).some(
        input => (input as HTMLInputElement).value.trim() !== ''
      );
      const pntaChecked = group.querySelector('.pnta-checkbox input:checked') !== null;
      isAnswered = hasValue || pntaChecked;
    }

    group.classList.toggle('answered', isAnswered);
  });
}

// ============================================================================
// STORE-BASED FALLBACK (for badge on non-profile pages)
// ============================================================================

/** Profile shape used by the store-based counter */
export interface ProfileData {
  first_name?: string | null;
  birth_date?: string | null;
  bio?: string | null;
  city?: string | null;
  height_cm?: number | null;
  gender_id?: number | null;
  relationship_status_id?: number | null;
  profile_json?: {
    about?: { headline?: string };
    body?: { body_type?: string };
    lifestyle?: {
      smoking?: string;
      drinking?: string;
      cannabis?: string;
      drugs?: string;
      exercise?: string;
    };
    kids?: {
      has_kids?: string;
      wants_more_kids?: string;
    };
    background?: {
      education_level?: string;
      employment_status?: string;
      income_range?: string;
    };
    identity?: {
      religion?: string;
      political_view?: string;
      ethnicity?: string[];
      zodiac_sign?: string;
      hometown?: string;
    };
    relationship?: {
      structure?: string;
      open_to_relocation?: string;
      open_to_long_distance?: string;
    };
    sexual?: {
      role?: string[];
      dynamics?: string[];
    };
  } | null;
}

/**
 * Count unanswered questions from store data (fallback when profile DOM is absent).
 * NOTE: This list may undercount compared to the DOM auto-discovery counter.
 * The DOM counter is the source of truth when available.
 */
export function countUnansweredQuestions(profile: ProfileData | null | undefined): number {
  if (!profile) return 20; // rough estimate when no data

  let unanswered = 0;

  // Scalar fields
  const scalars: Array<() => unknown> = [
    () => profile.gender_id,
    () => profile.relationship_status_id,
    () => profile.profile_json?.body?.body_type,
    () => profile.profile_json?.lifestyle?.smoking,
    () => profile.profile_json?.lifestyle?.drinking,
    () => profile.profile_json?.lifestyle?.cannabis,
    () => profile.profile_json?.lifestyle?.drugs,
    () => profile.profile_json?.lifestyle?.exercise,
    () => profile.profile_json?.kids?.has_kids,
    () => profile.profile_json?.kids?.wants_more_kids,
    () => profile.profile_json?.background?.education_level,
    () => profile.profile_json?.background?.employment_status,
    () => profile.profile_json?.background?.income_range,
    () => profile.profile_json?.identity?.religion,
    () => profile.profile_json?.identity?.political_view,
    () => profile.profile_json?.identity?.zodiac_sign,
    () => profile.profile_json?.relationship?.structure,
    () => profile.profile_json?.relationship?.open_to_relocation,
    () => profile.profile_json?.relationship?.open_to_long_distance,
  ];
  for (const accessor of scalars) {
    const val = accessor();
    if (val === null || val === undefined || val === '') unanswered++;
  }

  // Array fields
  const arrays: Array<() => unknown[] | undefined> = [
    () => profile.profile_json?.identity?.ethnicity,
    () => profile.profile_json?.sexual?.role,
    () => profile.profile_json?.sexual?.dynamics,
  ];
  for (const accessor of arrays) {
    if (!accessor()?.length) unanswered++;
  }

  // Text fields (check for non-empty, non-null value)
  const textFields: Array<() => unknown> = [
    () => profile.profile_json?.about?.headline,
    () => profile.profile_json?.identity?.hometown,
  ];
  for (const accessor of textFields) {
    const val = accessor();
    if (val === null || val === undefined || val === '') unanswered++;
  }
  // Profile column text fields
  if (!profile.first_name) unanswered++;
  if (!profile.birth_date) unanswered++;
  if (!profile.bio) unanswered++;
  if (!profile.city) unanswered++;
  if (!profile.height_cm) unanswered++;

  return unanswered;
}
